(ns dashboard.utils
  (:require
   [clojure.string :as string :refer
    [join capitalize lower-case split replace starts-with?]]
   [reitit.frontend.easy :as rtfe]
   [re-frame.core :refer [subscribe]]
   [testdouble.cljs.csv :as csv]
   [cemerick.url :refer [url]]
   ["d3-format" :as d3Format]
   ["date-fns" :refer
    [format parse isSaturday isSunday intervalToDuration isDate formatISO]]
   ["date-fns-tz" :refer [utcToZonedTime formatInTimeZone zonedTimeToUtc]]
   [goog.object])
  (:import [goog.async Debouncer]))

;; -------------------------
;; Routes

(defn href
  "Return relative url for given route. Url can be used in HTML links."
  ([k]
   (href k nil nil))
  ([k params]
   (href k params nil))
  ([k params query]
   (rtfe/href k params query)))

;; -------------------------
;; Helpers

;; https://gist.github.com/thenonameguy/714b4a4aa5dacc204af60ca0cb15db43
(defn distinct-by
  "Returns a stateful transducer that removes elements by calling f on each step as a uniqueness key.
   Returns a lazy sequence when provided with a collection."
  ([f]
   (fn [rf]
     (let [seen (volatile! #{})]
       (fn
         ([] (rf))
         ([result] (rf result))
         ([result input]
          (let [v (f input)]
            (if (contains? @seen v)
              result
              (do (vswap! seen conj v)
                  (rf result input)))))))))
  ([f xs]
   (sequence (distinct-by f) xs)))

(defn halfwidth->fullwidth [s]
  (.replace s #"[\uff01-\uff5e]"
            (fn [ch] (.fromCharCode js/String (- (.charCodeAt ch 0) 0xfee0)))))

(defn ->json [v]
  (.stringify js/JSON (clj->js v)))

(defn json-> [v & [{:keys [keywordize-keys] :or {keywordize-keys true}}]]
  (js->clj (.parse js/JSON v) :keywordize-keys keywordize-keys))

(defn j->c [x]
  (js->clj x :keywordize-keys true))

(defn listen [query-key & query-args]
  (if query-args
    @(subscribe (into [] (cons query-key query-args)))
    @(subscribe [query-key])))

(defn fmap
  "Apply function f to map m values"
  [f m]
  (into {} (for [[k v] m] [k (f v)])))

(defn index-by
  "Transform an array of items where one key is unique into a hash-map"
  [col k & [{:keys [convert-fn] :or {convert-fn str}}]]
  (fmap first (group-by #(convert-fn (get % k)) col)))

(defn index-of
  "Return the index of pred in a vector."
  [coll value]
  (some (fn [[idx item]] (if (= value item) idx))
        (map-indexed vector coll)))

(defn in?
  "true if coll contains elm"
  [coll elm]
  (some #(= elm %) coll))

(defn cs [& names]
  (join " " (filter identity names)))

(defn format-duration
  "Format a duration in seconds."
  [value]
  (let [hours (quot value 3600)
        minutes (quot (rem value 3600) 60)
        seconds (int (rem value 60))]
    (if (and (some? value) (not (js/isNaN value)) (not (zero? value)))
      (str (when (> hours 0)
             (str hours "h "))
           (when (> minutes 0)
             (str minutes "m "))
           ;; Don't display seconds if we have hours
           (when (and (> seconds 0) (= hours 0))
             (str seconds "s")))
      "-")))

(defn format-duration-ms [value]
  (if (zero? value)
    0
    (format-duration (/ value 1000))))

(defn format-percent [value & {:keys [digits sign] :or {digits 1 sign true}}]
  (if (and (some? value) (not (js/isNaN value)))
    (if sign
      ((.format d3Format (str "." digits "~%")) value)
      ((.format d3Format (str "." digits "r")) (* 100 value)))
    "-"))

(defn format-number [n & {:keys [digits unit] :or {unit ""}}]
  (if (and (some? n) (not (js/isNaN n)))
    (if (some? digits)
      (str ((.format d3Format (str ",." digits "f")) n) unit)
      (str ((.format d3Format ",~r") n) unit))
    "-"))

(defn slugify [s]
  (-> s
      (lower-case)
      (replace " " "-")))

(defn escape [s]
  (if (string? s)
    (string/escape s
                   {\newline "\\n"
                    \tab "\\t"
                    \backspace "\\b"
                    \formfeed "\\f"
                    \return "\\r"
                    \u0022 \u0027    ;; double quote replaced by single quote
                    \u005c "\\\\"})  ;; backslash
    s))

(defn ->cap [s]
  (when (some? s)
    (capitalize (lower-case (join " " (split s "_"))))))

(defn ->ns-first [k]
  (-> k
      (namespace)
      (split ".")
      (first)))

(defn ->ns-second [k]
  (-> k
      (namespace)
      (split ".")
      (second)))

(defn most-frequent [items]
  (->> items
       frequencies
       (sort-by val)
       reverse
       (first)
       (first)))

(defn most-frequent-n [n items]
  (->> items
       frequencies
       (sort-by val)
       reverse
       (take n)
       (map first)))

(defn average
  [numbers]
  (if (empty? numbers)
    0
    (/ (reduce + numbers) (count numbers))))

(defn vec-remove
  "Remove elem in coll by index."
  [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn vec-add
  "Add elem in coll by index."
  [coll pos el]
  (concat (subvec coll 0 pos) [el] (subvec coll pos)))

(defn vec-move
  "Move elem in coll by index"
  [coll pos1 pos2]
  (let [el (nth coll pos1)
        pos2' (if (> pos2 pos1) pos2 pos2)]
    (if (= pos1 pos2)
      coll
      (into [] (vec-add (vec-remove coll pos1) pos2' el)))))

(defn vec-swap
  "Swap position of 2 elem in a vector by index."
  [v pos1 pos2]
  (assoc v pos2 (v pos1) pos1 (v pos2)))

(defn parse-int [s]
  (let [v (js/parseInt s)]
    (when (not (js/isNaN v))
      v)))

;; -------------------------
;; Colors

(defn hex->rgb [s]
  (->> (subs s 1 7)
       (partition 2)
       (map join)
       (mapv #(js/parseInt % 16))))

(defn hex->rgba [s opacity]
  (into [] (conj (hex->rgb s) opacity)))

;; -------------------------
;; URL utils

(defn keywordize-keys
  "Recursively transforms all map keys from strings to keywords."
  [m]
  (let [f (fn [[k v]]
            (if (string? k) [(keyword k) v] [k v]))]
    (clojure.walk/postwalk (fn [x] (if (map? x) (into {} (map f x)) x)) m)))

(defn is-only-digits? [s]
  (= (re-find #"\d+" s) s))

(defn parse-values
  "Recursively transforms all map values to the right type."
  [m]
  (let [f (fn [[k v]]
            (cond
              (and (string? v) (= v "")) [k nil]
              (and (string? v) (= v "false")) [k false]
              (and (string? v) (= v "true")) [k true]
              (and (string? v) (is-only-digits? v)) [k (js/parseInt v)]
              :else [k v]))]
    (clojure.walk/postwalk (fn [x] (if (map? x) (into {} (map f x)) x)) m)))

(defn parse-params
  "Parse URL parameters into a hashmap"
  []
  (let [query (:query (url (.-location js/window)))]
    (or
     (->> query
          (keywordize-keys)
          (parse-values))
     {})))

(defn remove-nil
  "Remove nil keys from map"
  [m]
  (into {} (->> m
                (remove (comp nil? second))
                (remove (comp #(= "" %) second)))))

;; -------------------------
;; Throttle & Debounce

(defn debounce [f interval]
  (let [dbnc (Debouncer. f interval)]
    ;; We use apply here to support functions of various arities
    (fn [& args] (.apply (.-fire dbnc) dbnc (to-array args)))))

;; https://github.com/metosin/komponentit/blob/master/src/cljs/komponentit/clipboard.cljs
(defn copy-text [text]
  (let [el (js/document.createElement "textarea")
        prev-focus-el js/document.activeElement
        y-pos (or (.. js/window -pageYOffset)
                  (.. js/document -documentElement -scrollTop))]
    (set! (.-style el) #js {:position "absolute"
                            :left "-9999px"
                            :top (str y-pos "px")
                            ;; iOS workaround?
                            :fontSize "12pt"
                            ;; reset box-model
                            :border "0"
                            :padding "0"
                            :margin "0"})
    (set! (.-value el) text)
    (.addEventListener el "focus" (fn [_] (.scrollTo js/window 0 y-pos)))
    (js/document.body.appendChild el)
    (.setSelectionRange el 0 (.. el -value -length))
    (.focus el)
    (js/document.execCommand "copy")
    (.blur el)
    (when prev-focus-el
      (.focus prev-focus-el))
    (.removeAllRanges (.getSelection js/window))
    (js/window.document.body.removeChild el)))

;; -------------------------
;; GraphQL

(def graphql-request-format
  {:write ->json
   :content-type "application/graphql"})

;; -------------------------
;; CSV & XLS Export

(defn download-as-csv [file-name rows]
  (.click
   (doto (js/document.createElement "a")
     (goog.object/set "href" (js/URL.createObjectURL
                              (js/Blob. #js [(js/Uint8Array. [0xEF 0xBB 0xBF])
                                             (csv/write-csv rows :quote? true)]
                                        #js {:type "text/plain;charset=utf-8"})))
     (goog.object/set "download" file-name))))

(defn download-as-xlsx [file-name rows]
  (let [wb (.. js/XLSX -utils (book_new))
        ws (.. js/XLSX -utils (aoa_to_sheet (clj->js rows)))
        _ (.. wb -SheetNames (push "取り込みシフト"))
        _ (doto (.-Sheets wb)
            (goog.object/set "取り込みシフト" ws))
        xlsbin (.. js/XLSX (write wb #js {:bookType "xlsx"
                                          :type "binary"}))
        xlsarray (->> (range (.-length xlsbin))
                      (mapv #(or (.. xlsbin (charCodeAt %)) 0xFF))
                      (clj->js)
                      (js/Uint8Array.))
        xlsblob (js/Blob. #js [xlsarray]
                          #js {:type "application/octet-stream"})]
    (.click
     (doto (js/document.createElement "a")
       (goog.object/set "href" (js/URL.createObjectURL
                                xlsblob))
       (goog.object/set "download" file-name)))))

;; -------------------------
;; Public Holidays

(defn isHoliday [date]
  (let [holidays-date-set (->> (listen :google/holidays-jp)
                               (map (comp :date :start))
                               (into #{}))]
    (some? (holidays-date-set (format date "yyyy-MM-dd")))))

(defn isOff [date]
  (or (isSaturday date) (isSunday date) (isHoliday date)))

;; -------------------------
;; Timezone utils

(defn format-time-in-timezone [{:keys [time date timezone]}]
  (-> (zonedTimeToUtc (parse time "HH:mm" date) timezone)
      (formatInTimeZone timezone  "yyyy-MM-dd'T'HH:mm:ssXXX")))

(defn format-in-space-timezone
  [{:keys [date timezone]
    :or {timezone (listen :dashboard.subs/timezone)}}]
  (-> date
      (zonedTimeToUtc timezone)
      (formatInTimeZone timezone "yyyy-MM-dd'T'HH:mm:ssXXX")))

(defn format-hour
  [date]
  (let [tz (listen :dashboard.subs/timezone)
        ts (if (some? tz) (utcToZonedTime date tz) date)]
    (when (some? date)
      (format ts "HH:00" (clj->js {:timeZone tz})))))

(defn format-time
  [date]
  (let [tz (listen :dashboard.subs/timezone)
        ts (if (some? tz) (utcToZonedTime date tz) date)]
    (when (some? date)
      (format ts "HH:mm" (clj->js {:timeZone tz})))))

(defn format-day
  "Format a JS Date object to a localized day of the month."
  [date & [{:keys [month?] :or {month? false}}]]
  (format date (if (= (listen :dashboard.subs/locale) :ja)
                 (if month? "MMMMdo" "do")
                 (if month? "MMM d" "d"))
          #js {:locale (listen :dashboard.subs/date-fns-locale)}))

(defn format-week
  "Format a JS Date object to a localized week."
  [date]
  (format date (if (= (listen :dashboard.subs/locale) :ja) "I'週'" "'W'I")
          #js {:locale (listen :dashboard.subs/date-fns-locale)}))

(defn format-month
  "Format a JS Date object to a localized month of the year."
  [date]
  (format date (if (= (listen :dashboard.subs/locale) :ja) "yyyy年 LLL" "MMMM yyyy")
          #js {:locale (listen :dashboard.subs/date-fns-locale)}))

(defn format-date
  "Format a JS Date object to a localized day of the month."
  [date & [{:keys [day-of-week?] :or {day-of-week? false}}]]
  (let [date-fns-format (if day-of-week? "PPPP" "PPP")]
    [:time
     {:dateTime (format date "yyyy-MM-dd")}
     (if (= (listen :dashboard.subs/locale) :ja)
       (str (format date "PPP" #js {:locale (listen :dashboard.subs/date-fns-locale)})
            (when day-of-week?
              (str "（"
                   (format date "EEE" #js {:locale (listen :dashboard.subs/date-fns-locale)})
                   "）")))
       (format date date-fns-format #js {:locale (listen :dashboard.subs/date-fns-locale)}))]))

(defn format-iso
  "A safe and smart ISO format for Date object"
  [date]
  (when (and (some? date))
    (if (isDate date)
      (formatISO date)
      (formatISO (js/Date. date)))))

;; -------------------------
;; Duration

;; FIXME: Limited to duration < 1 month
(defn duration-in-hours [{:keys [start end]}]
  (let [duration (intervalToDuration #js {:start start :end end})
        days (aget duration "days")
        hours (aget duration "hours")
        minutes (aget duration "minutes")]
    (+ (* 24 days) hours (/ minutes 60))))

;; -------------------------
;; CSV

(defn secure-csv-lines
  "Add ' at the begining of the CSV string if it starts with = + - @
   to avoid CSV injection, else return the string as is.
   If the string is nil, return nil."
  [text-str]
  (when text-str
    (if (->> (map #(starts-with? text-str %) ["=" "+" "-" "@"])
             (some true?))
      (str "'" text-str)
      text-str)))

(defn import-csv-file
  [{:keys [file on-load-success on-load-fail]
    :or {on-load-success #(js/console.log %)
         on-load-fail #(js/console.log %)}}]
  (let [reader (js/FileReader.)
        onload (fn [onload-ev]
                 (try
                   (let [contents (-> onload-ev .-target .-result)]
                     (on-load-success (csv/read-csv contents)))
                   (catch js/Error e
                     (on-load-fail e))))]
    (aset reader "onload" onload)
    (.readAsText reader file)))
